import React, {useContext, useEffect, useMemo, useState} from "react";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {useTranslation} from "react-i18next";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {ProjectApi} from "../../../api/authority/generated/index";
import toast from "react-hot-toast";
import {ChannelGridSection} from "./ChannelGridSection.jsx";
import {useMarketingColumns} from "./channelGridColumnSetup.js";
import {ALWAYS_LAST_KEY} from "../../../utils/dataGridSorting.js";
import updateTotal, {calculateMarketingChannelResults} from "../../../utils/datagridTotal.js";
import GridSection from "../../../components/grid/GridSection.jsx";
import Modal from "../../../components/Modal.jsx";
import {FilterContext} from "../../../components/filters/DashboardFilterContextProvider.jsx";
import FormError from "../../../components/form/FormError.jsx";
import useUserSettings from "../../../utils/useUserSettings.js";
import DataGrid2 from "../../../components/datagrid/DataGrid2.jsx";
import {SnapshotCompletenessNotice} from "../../admin/ReportingLab.jsx";
import {allowsCampaignBreakdown} from "../../../utils/subscriptionFeatures.js";

export default function ChannelGrid({project, gridType, collection = undefined}) {
    const campaignBreakdownAllowed = allowsCampaignBreakdown(project);

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let [loadedCollection, setLoadedCollection] = useState(null);

    let [channelModal, setChannelModal] = useState(null);
    let [channelModalData, setChannelModalData] = useState(null);

    let [userSettings] = useUserSettings();

    useEffect(() => {
        if (collection !== undefined) {
            return;
        }

        let active = true;
        setLoadedCollection(null);

        new ProjectApi(getAuthorityConfiguration())
            .marketingChannelOverviews({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                },
            })
            .then((result) => {
                if (active) {
                    setLoadedCollection(result);
                }
            })
            .catch((e) => {
                if (!active) {
                    return;
                }

                setLoadedCollection({items: []});
                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [
        collection,
        project.id,
        since.format('YYYY-MM-DD'),
        until.format('YYYY-MM-DD'),
        centresAllowListed,
        domainsAllowListed,
    ]);

    let effectiveCollection = collection === undefined ? loadedCollection : collection;
    let channelList = effectiveCollection?.items ?? null;


    let [channelTotal, setChannelTotal] = useState({});

    useEffect(() => {
        setChannelTotal({});
    }, [effectiveCollection]);

    let columns = useMarketingColumns(gridType, project);

    let channelTotalResult = useMemo(() => {
        let totalRow = {
            name: t('project.total'),
        };

        totalRow[ALWAYS_LAST_KEY] = true;

        let foundAny = false;

        for (let subRow of Object.values(channelTotal)) {
            for (let column of Object.values(columns)) {
                totalRow = updateTotal(totalRow, column, subRow[column.name]);
                foundAny = true;
            }
        }

        totalRow = calculateMarketingChannelResults(userSettings, totalRow);

        if (!foundAny) {
            return [];
        }

        return [totalRow];
    }, [channelTotal, userSettings]);

    useEffect(() => {
        if (channelModal === null || !campaignBreakdownAllowed) {
            setChannelModalData(null);
            return;
        }

        new ProjectApi(getAuthorityConfiguration())
            .marketingChannelCampaignData({
                projectId: project.id,
                channelId: channelModal.channel.id,
                campaignRemoteId: channelModal.campaign.remoteId,
                dateRangeInput: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                },
            })
            .then(setChannelModalData)
            .catch((e) => {
                et(e).then((text) => setChannelModalData({
                    error: text
                }));
            });
    }, [project.id, since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'), channelModal, campaignBreakdownAllowed]);

    return [
        <Modal
            visible={channelModal !== null}
            setVisible={() => setChannelModal(null)}
            loading={channelModalData === null}
            title={channelModalData?.name}
            subtitle={channelModalData?.remoteId}
            key={'modal'}
        >
            {channelModalData?.error && <FormError error={channelModalData?.error}/>}
            {channelModalData?.remoteId && channelModal?.campaign && <div>
                <DataGrid2
                    group={'channel-grid-modal'}
                    dataSource={[channelModal?.campaign]}
                    columns={columns}
                    showHeader={true}
                    hasTotal={true}
                />
                <div className="space-y-2 pt-3">
                    {Object.values(channelModalData.adSets).map((adSet) => {
                        adSet[ALWAYS_LAST_KEY] = true;
                        return <div
                            className="shadow-lg rounded-sm border px-5 py-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                            key={'ad-set' + adSet.remoteId}
                        >
                            <div className="md:flex justify-between items-center space-y-4 md:space-y-0 space-x-2">
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div>
                                        <div className="inline-flex font-semibold text-slate-800 dark:text-slate-100">
                                            {adSet.name}
                                        </div>
                                        <div className="text-xs">{adSet.remoteId}</div>
                                    </div>
                                </div>
                            </div>
                            <div className={'pt-2'}>
                                <DataGrid2
                                    group={'channel-grid-modal'}
                                    dataSource={[...adSet.ads, adSet]}
                                    columns={columns}
                                    showHeader={true}
                                    hasTotal={true}
                                />
                            </div>
                        </div>;

                    })}
                </div>
            </div>}
        </Modal>,
        <LoadingComponent className={'col-span-12'} loading={channelList === null} key={'table'}>
            <SnapshotCompletenessNotice snapshots={effectiveCollection?.snapshot} className="mb-4"/>

            {channelList && channelList.map((channel) => <ChannelGridSection
                key={channel.id}
                gridType={gridType}
                channel={{id: channel.id, name: channel.name}}
                channelData={channel.overview}
                setChannelTotal={setChannelTotal}
                setModalCampaignRow={campaignBreakdownAllowed ? (campaign) => {
                    setChannelModalData(null);
                    setChannelModal({
                        channel: channel,
                        campaign: campaign,
                    });
                } : undefined}
            />)}

            {channelTotalResult.length > 0 && <GridSection
                title={t('project.total')}
                gridType={gridType}
            >
                {<DataGrid2
                    group="marketing-profitability"
                    dataSource={channelTotalResult}
                    columns={columns}
                    showHeader={true}
                    hasTotal={true}
                />}
            </GridSection>}
        </LoadingComponent>
    ];
}
